import { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Story } from '@/utils/storiesData';
import { storyCategories } from '@/data/storyCategories';

export const useBrowseContent = (stories: Story[], categories: string[]) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const [filteredStories, setFilteredStories] = useState<Story[]>(stories);
  
  // Calculate story counts per category
  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {
      'all': stories.length
    };
    
    stories.forEach(story => {
      const category = story.category.toLowerCase();
      counts[category] = (counts[category] || 0) + 1;
    });
    
    return counts;
  }, [stories]);
  
  // Get active category description
  const activeCategoryDescription = useMemo(() => {
    // Find the category info by slug or title match
    const categoryInfo = storyCategories.find(
      cat => cat.slug === activeCategory || cat.title.toLowerCase().replace(/\s+/g, '-') === activeCategory
    );
    
    return categoryInfo?.description || '';
  }, [activeCategory]);
  
  useEffect(() => {
    window.scrollTo(0, 0);

    // Get parameters from URL query parameters
    const searchParams = new URLSearchParams(location.search);
    const categoryParam = searchParams.get('category');

    console.log('URL category parameter:', categoryParam);
    console.log('Available categories:', categories);

    // Set active category if it exists in our categories list or matches a story category slug
    if (categoryParam) {
      // Check if the categoryParam matches any story category slug
      const matchingCategory = storyCategories.find(cat => cat.slug === categoryParam);
      if (matchingCategory) {
        setActiveCategory(categoryParam);
      } else if (categories.includes(categoryParam.toLowerCase())) {
        setActiveCategory(categoryParam.toLowerCase());
      } else {
        setActiveCategory('all');
      }
    } else {
      setActiveCategory('all');
    }
    
    // Handle tag parameter
    const tagParam = searchParams.get('tag');
    if (tagParam) {
      setActiveTag(tagParam);
    } else {
      setActiveTag(null);
    }
  }, [location.search, categories]);
  
  useEffect(() => {
    console.log('Filtering stories for category:', activeCategory);
    console.log('Available stories:', stories.length);
    
    // Filter stories based on active category and tag
    let filtered = stories;
    
    // Filter by category first
    if (activeCategory !== 'all') {
      filtered = filtered.filter(story => {
        const storyCategory = story.category.toLowerCase();
        const storyCategorySlug = story.category.toLowerCase().replace(/\s+/g, '-');
        
        // Match against category name, lowercase category, or slug format
        return (
          storyCategory === activeCategory.toLowerCase() ||
          storyCategorySlug === activeCategory ||
          story.category.toLowerCase().replace(/\s+/g, '-') === activeCategory
        );
      });
    }
    
    // Then filter by tag if selected
    if (activeTag) {
      filtered = filtered.filter(story => 
        story.tags && story.tags.includes(activeTag)
      );
    }
    
    console.log('Filtered stories:', filtered.length);
    setFilteredStories(filtered);
  }, [activeCategory, activeTag, stories]);
  
  const handleCategoryChange = (category: string) => {
    setActiveCategory(category);
    
    // Update URL
    const searchParams = new URLSearchParams(location.search);
    if (category === 'all') {
      searchParams.delete('category');
    } else {
      searchParams.set('category', category);
    }
    
    // Keep the tag parameter if it exists
    if (activeTag) {
      searchParams.set('tag', activeTag);
    }
    
    navigate({
      pathname: location.pathname,
      search: searchParams.toString()
    });
  };
  
  const handleTagChange = (tag: string | null) => {
    setActiveTag(tag);
    
    // Update URL
    const searchParams = new URLSearchParams(location.search);
    if (tag === null) {
      searchParams.delete('tag');
    } else {
      searchParams.set('tag', tag);
    }
    
    // Keep the category parameter if it's not 'all'
    if (activeCategory !== 'all') {
      searchParams.set('category', activeCategory);
    }
    
    navigate({
      pathname: location.pathname,
      search: searchParams.toString()
    });
  };
  
  return {
    filteredStories,
    activeCategory,
    activeTag,
    categoryCounts,
    activeCategoryDescription,
    handleCategoryChange,
    handleTagChange
  };
};

export default useBrowseContent;
